import React, { useCallback } from 'react';
import { F2120, F2121, pw2202Api } from '../../../services/pw2202Api';
import { fmt } from '../helpers';

interface Tab2Props {
  formData: Partial<F2120>;
  details: Partial<F2121>[];
  selectedDetailRow: number;
  isEditing: boolean;
  getField: (key: keyof F2120) => F2120[keyof F2120];
  setDetails: React.Dispatch<React.SetStateAction<Partial<F2121>[]>>;
  setSelectedDetailRow: (idx: number) => void;
  addDetailRow: () => void;
  deleteDetailRow: () => void;
  setDetailField: (rowIdx: number, key: keyof F2121, value: F2121[keyof F2121]) => void;
}

/** Tab 2: Detail Grid (明細) */
const Tab2Detail: React.FC<Tab2Props> = React.memo(({
  formData, details, selectedDetailRow, isEditing,
  getField, setDetails, setSelectedDetailRow,
  addDetailRow, deleteDetailRow, setDetailField,
}) => {

  /** Handle product code blur: auto-fill name, unit, price */
  const handleProductBlur = useCallback(async (rowIdx: number, code: string) => {
    if (!code.trim()) return;
    const info = await pw2202Api.getProductInfo(code);
    if (info) {
      setDetails(prev => {
        const next = [...prev];
        const row = { ...next[rowIdx] };
        row.f2121_11a = info.name;
        row.f2121_12 = info.unit;
        row.f2121_14 = info.price;
        // recalc amount
        const qty = row.f2121_13 || 0;
        row.f2121_15 = Math.round(qty * info.price * 100) / 100;
        next[rowIdx] = row;
        return next;
      });
    }
  }, [setDetails]);

  return (
    <div className="tab-content-section">
      {/* Detail buttons */}
      <div className="grid-header">
        <h3 className="section-title" style={{ margin: 0, border: 'none', padding: 0 }}>退回明細</h3>
        <div className="grid-actions">
          <button className="btn btn-sm" onClick={addDetailRow} disabled={!isEditing}>明細新增</button>
          <button className="btn btn-sm btn-danger" onClick={deleteDetailRow}
            disabled={!isEditing || details.length === 0}>明細刪除</button>
        </div>
      </div>

      {/* Detail Grid */}
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 40, textAlign: 'center' }} data-contract-field="f2121_02">項次</th>
              <th style={{ width: 100 }} data-contract-field="f2121_11">產品代號</th>
              <th style={{ width: 160 }} data-contract-field="f2121_11a">產品名稱</th>
              <th style={{ width: 50 }} data-contract-field="f2121_12">單位</th>
              <th style={{ width: 90, textAlign: 'right' }} data-contract-field="f2121_13">數量</th>
              <th style={{ width: 90, textAlign: 'right' }} data-contract-field="f2121_14">單價</th>
              <th style={{ width: 100, textAlign: 'right' }} data-contract-field="f2121_15">金額</th>
              <th style={{ width: 100 }} data-contract-field="f2121_21">訂單編號</th>
              <th style={{ width: 60, textAlign: 'right' }} data-contract-field="f2121_22">訂單序號</th>
            </tr>
          </thead>
          <tbody>
            {details.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: 'center', color: '#999', padding: 20 }}>無明細資料</td></tr>
            )}
            {details.map((d, idx) => (
              <tr
                key={idx}
                className={idx === selectedDetailRow ? 'row-selected' : ''}
                onClick={() => setSelectedDetailRow(idx)}
              >
                {/* 項次 (readonly) */}
                <td style={{ textAlign: 'center' }}>{d.f2121_02}</td>

                {/* 產品代號 (lookup on blur) */}
                <td>
                  <input
                    type="text"
                    value={d.f2121_11 || ''}
                    onChange={e => setDetailField(idx, 'f2121_11', e.target.value)}
                    onBlur={e => handleProductBlur(idx, e.target.value)}
                    disabled={!isEditing}
                    maxLength={12} data-contract-field="f2121_11"
                  />
                </td>

                {/* 產品名稱 (readonly) */}
                <td>
                  <input type="text" value={d.f2121_11a || ''} disabled data-contract-field="f2121_11a" />
                </td>

                {/* 單位 (readonly) */}
                <td>
                  <input type="text" value={d.f2121_12 || ''} disabled data-contract-field="f2121_12" />
                </td>

                {/* 數量 [T-F03] */}
                <td>
                  <input
                    type="number"
                    value={d.f2121_13 ?? ''}
                    onChange={e => setDetailField(idx, 'f2121_13', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                    style={{ textAlign: 'right' }}
                    step="0.01" data-contract-field="f2121_13"
                  />
                </td>

                {/* 單價 [T-F03] */}
                <td>
                  <input
                    type="number"
                    value={d.f2121_14 ?? ''}
                    onChange={e => setDetailField(idx, 'f2121_14', parseFloat(e.target.value) || 0)}
                    disabled={!isEditing}
                    style={{ textAlign: 'right' }}
                    step="0.01" data-contract-field="f2121_14"
                  />
                </td>

                {/* 金額 (readonly, auto-calc) */}
                <td style={{ textAlign: 'right' }}>
                  {fmt(d.f2121_15)}
                </td>

                {/* 訂單編號 */}
                <td>
                  <input
                    type="text"
                    value={d.f2121_21 || ''}
                    onChange={e => setDetailField(idx, 'f2121_21', e.target.value)}
                    disabled={!isEditing}
                    maxLength={12}
                    data-contract-field="f2121_21"
                  />
                </td>

                {/* 訂單序號 */}
                <td>
                  <input
                    type="number"
                    value={d.f2121_22 ?? ''}
                    onChange={e => setDetailField(idx, 'f2121_22', parseInt(e.target.value) || 0)}
                    disabled={!isEditing}
                    style={{ textAlign: 'right', width: 50 }}
                    data-contract-field="f2121_22"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Below-grid: summary + remark */}
      <div style={{ display: 'flex', gap: 24, marginTop: 8, fontSize: 13, color: 'rgba(0,0,0,0.65)', alignItems: 'center' }}>
        <span>總項數: <strong>{getField('f2120_16') as number || details.length}</strong></span>
        <span>明細金額合計: <strong>{fmt(getField('f2120_17') as number)}</strong></span>
      </div>

      {/* 明細摘要 (F2121_18 for selected row) */}
      <div style={{ marginTop: 12 }}>
        <label
          style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)', marginBottom: 4, display: 'block' }}
          data-contract-field="f2121_18"
        >
          明細摘要{details.length > 0 ? ` (第 ${selectedDetailRow + 1} 項)` : ''}
        </label>
        <input
          type="text"
          value={details[selectedDetailRow]?.f2121_18 || ''}
          onChange={e => setDetailField(selectedDetailRow, 'f2121_18', e.target.value)}
          disabled={!isEditing || details.length === 0}
          maxLength={60}
          style={{ width: '100%' }}
          data-contract-field="f2121_18"
        />
      </div>
    </div>
  );
});

Tab2Detail.displayName = 'Tab2Detail';
export default Tab2Detail;
